<script lang="ts">
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { ArrowLeftRight } from 'lucide-svelte';
  import { catalogApi } from '$lib/api/catalog';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import type { DuplicateGroup, MergePreview } from '$lib/types';

  const { onClose }: { onClose: () => void } = $props();

  const queryClient = useQueryClient();
  const duplicatesQuery = createQuery(() => ({
    queryKey: ['catalog-duplicates'],
    queryFn: () => catalogApi.duplicates()
  }));

  let activeGroup = $state<DuplicateGroup | null>(null);
  let winnerId = $state<number | null>(null);
  let preview = $state<MergePreview | null>(null);
  let error = $state('');

  function openGroup(group: DuplicateGroup) {
    activeGroup = group;
    winnerId = group.products[0]?.id ?? null;
    preview = null;
    error = '';
  }

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['catalog-duplicates'] });
    queryClient.invalidateQueries({ queryKey: ['catalog-products'] });
    queryClient.invalidateQueries({ queryKey: ['catalog-categories'] });
    queryClient.invalidateQueries({ queryKey: ['catalog-health'] });
  }

  const previewMutation = createMutation(() => ({
    mutationFn: ({ winner, losers }: { winner: number; losers: number[] }) => catalogApi.mergePreview(winner, losers),
    onSuccess: (data) => (preview = data),
    onError: (err: unknown) => (error = getErrorMessage(err))
  }));

  const mergeMutation = createMutation(() => ({
    mutationFn: ({ winner, losers }: { winner: number; losers: number[] }) => catalogApi.merge(winner, losers),
    onSuccess: (data) => {
      invalidate();
      toast.success(
        `Merged ${data.merged} duplicate${data.merged === 1 ? '' : 's'} — ${data.line_items} line item${data.line_items === 1 ? '' : 's'} re-pointed.`
      );
      activeGroup = null;
      preview = null;
    },
    onError: (err: unknown) => (error = getErrorMessage(err))
  }));

  function loserIds(): number[] {
    if (!activeGroup || winnerId === null) return [];
    return activeGroup.products.filter((p) => p.id !== winnerId).map((p) => p.id);
  }

  function runPreview() {
    error = '';
    preview = null;
    const losers = loserIds();
    if (winnerId === null || losers.length === 0) return;
    previewMutation.mutate({ winner: winnerId, losers });
  }

  function confirmMerge() {
    const losers = loserIds();
    if (winnerId === null || losers.length === 0) return;
    mergeMutation.mutate({ winner: winnerId, losers });
  }

  const pending = $derived(previewMutation.isPending || mergeMutation.isPending);
</script>

<Modal title="Duplicate products" onClose={pending ? () => {} : onClose} wide>
  {#if activeGroup}
    <div class="space-y-4">
      <p class="tym-small">
        Choose which product to <strong>keep</strong>. The rest are archived (never deleted) and every line item
        that referenced them is re-pointed to the winner.
      </p>

      <div class="space-y-2">
        {#each activeGroup.products as p (p.id)}
          <label class="flex items-center gap-2.5 rounded-[10px] border border-toyama-cloudy-gray p-2.5">
            <input
              type="radio"
              name="merge-winner"
              value={p.id}
              checked={winnerId === p.id}
              onchange={() => {
                winnerId = p.id;
                preview = null;
              }}
            />
            <div class="min-w-0 flex-1">
              <div class="text-[13px] font-medium text-navy">{p.name}</div>
              <div class="font-mono text-[11px] text-dark-gray">{p.part_number} &middot; {p.category || 'Uncategorised'}</div>
            </div>
            {#if winnerId === p.id}
              <span class="tym-badge tym-badge-success">Keep</span>
            {/if}
          </label>
        {/each}
      </div>

      {#if error}<p class="tym-error-state">{error}</p>{/if}

      {#if preview}
        <p class="tym-small">
          This will re-point <strong>{preview.line_items}</strong> line item{preview.line_items === 1 ? '' : 's'}
          onto "{preview.winner.part_number}" and archive {preview.losers.length} duplicate{preview.losers.length === 1
            ? ''
            : 's'}. This can't be undone automatically.
        </p>
      {/if}

      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={() => (activeGroup = null)} disabled={pending}>Back</button>
        {#if !preview}
          <button type="button" class="btn-secondary" disabled={pending} onclick={runPreview}>
            <ArrowLeftRight class="h-4 w-4" /> Preview merge
          </button>
        {:else}
          <button type="button" class="btn-primary" disabled={pending} onclick={confirmMerge}>
            {mergeMutation.isPending ? 'Merging…' : 'Confirm merge'}
          </button>
        {/if}
      </div>
    </div>
  {:else if duplicatesQuery.isLoading}
    <p class="tym-loading-state">Scanning for duplicate part numbers&hellip;</p>
  {:else if duplicatesQuery.isError}
    <p class="tym-error-state">{getErrorMessage(duplicatesQuery.error)}</p>
  {:else if !duplicatesQuery.data?.results.length}
    <p class="tym-small">No duplicate part numbers found — every active product's part number is unique.</p>
  {:else}
    <div class="space-y-2">
      {#each duplicatesQuery.data.results as group (group.key)}
        <button
          type="button"
          class="tym-card flex w-full items-center justify-between gap-3 p-3 text-left hover:border-toyama-teal"
          onclick={() => openGroup(group)}
        >
          <div class="min-w-0">
            <div class="text-[13px] font-medium text-navy">
              {group.products.map((p) => p.part_number).join(' / ')}
            </div>
            <div class="text-[11.5px] text-dark-gray">{group.products.map((p) => p.name).join(', ')}</div>
          </div>
          <span class="tym-badge tym-badge-warning shrink-0">{group.products.length}</span>
        </button>
      {/each}
    </div>
  {/if}
</Modal>
